import {PERMISSIONS, useUser} from "../../../core/context/UserContext";
import {useDataGrid} from "../../../core/components/data-grid/DataGridContext";
import {t} from "i18next";
import {dateFormat} from "../../../core/utils/date-utils";
import VoiceRecordRowMenu from "./VoiceRecordRowMenu";
import {Button, Popover, Radio, Select, Space, Tooltip, Typography} from "antd";
import ApiService from "../../../core/service/api-service";
import {CheckOutlined, PlayCircleOutlined, UploadOutlined} from "@ant-design/icons";
import {useEffect, useRef, useState} from "react";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import Colors from "../../../core/style/Colors";
import FileUtils from "../../../core/utils/file-utils";
import {useApp} from "../../../core/context/AppContext";
import AudioPlayerNavigator from "../AudioPlayer";
import {AudioOutlined, StopOutlined} from "@ant-design/icons";
import {v4} from "uuid";
import Crunker from "crunker";

export default function VoiceRecordGrid({studyId, listOther})
{
    const playerId = `voice-record-player-${studyId}`
    const {hasPermission} = useUser();
    const {addKeyEvent, removeKeyEvent, pressedKey} = useApp()
    const {setData, setPaginationResult, queryModel, setDataGridProps, setRowColor, refreshDataGrid, resultModel, setLoading} = useDataGrid();
    const [pbRate, setPbRate] = useState(1)
    const [seek, setSeek] = useState(1)
    const [action, setAction] = useState(null)
    const url = useRef(null)
    // Listenin ilk (sunucudaki) kaydı; dosyası ilk oynatmada indirilir.
    const firstRecord = useRef(null)
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState("00:00");
    const recordingInterval = useRef(null);
    const mediaRecorder = useRef(null);
    const [records, setRecords] = useState([]);

    // Yükleme göstergesi TABLONUN İÇİNDE (silent: küresel gösterge susar) — bkz. StudyRequestGrid.
    const getVoiceRecords = async () => {
        setLoading(true)
        let response = {}
        try {
            if(!listOther) {
                response = await ApiService.VoiceRecord.GetList(studyId, {silent: true});
            }
            else {
                response = await ApiService.VoiceRecord.GetOtherVoiceRecords(studyId, {silent: true});
            }
        }
        finally {
            setLoading(false)
        }
        if(response.success)
        {
            setData([
                ...records, ...response.data
            ])
            // İlk kaydın ses dosyası burada İNDİRİLMEZ (rapor ekranının açılışını uzatıyordu);
            // F8 ile oynatılmak istendiğinde çekilir (bkz. playF8).
            const recordId = response.data[0]?.id
            const studyId = response.data[0]?.studyId
            if(recordId && studyId) {
                url.current = null
                firstRecord.current = {studyId, recordId}
                setRowColor(recordId, Colors.selectedRow)
            }
        }
    }

    const uploadVoiceRec = async () => {
        const file = await FileUtils.getFileAsBase64({accept: 'mp3'});
        if(file)
        {
            const response = await ApiService.VoiceRecord.UploadBase64(studyId, {
                voiceRecordBase64: file.data,
                fileName: file?.fileName
            })

            if(response.success) {
                refreshDataGrid()
            }
        }
    }

    const playF8 = async () => {
        const player = document.getElementById(playerId)
        if(!player)
            return

        if(!player.paused)
        {
            player.pause()
            return
        }

        // Açılışta indirilmeyen ilk kaydın dosyası ilk oynatmada çekilir. Ref'ten okunur: mediaSession
        // olayları mount anındaki kapanışı taşıyor.
        if(!url.current && firstRecord.current) {
            const {studyId, recordId} = firstRecord.current
            url.current = await getUrl(studyId, recordId)
        }

        if(!url.current)
            return

        if(player.src !== url.current) {
            player.src = url.current
        }

        if (player.currentTime >= 2) {
            player.currentTime -= 2;
        }

        player.play()
    }

    const play = async () => {
        const player = document.getElementById(playerId)
        if(!player)
            return

        if(url.current && player.src !== url.current) {
            player.src = url.current
        }
        else if (player.currentTime >= 2 && player.paused) {
            player.currentTime -= 2;
        }


        if(player.paused) {
            player.play()
            player.playbackRate = pbRate
        }
    }

    const stop = () => {
        const player = document.getElementById(playerId);
        if (player && !player.paused) {
            player.pause();
        }
        if (isRecording) {
            stopRecording();
        }
    };

    const getUrl = async (studyId, recordId) => {
        const response = await ApiService.VoiceRecord.GetFile(studyId, recordId);
        if(response.success)
        {
            return response.data
        }
    }

    const backwardSeek = () => {

        const player = document.getElementById(playerId);
        if(player) {
            player.currentTime -= seek
        }
    }

    const playEvent = async () => {

        if(!url.current) {
            const recordId = resultModel?.data[0]?.id
            const studyId = resultModel?.data[0]?.studyId
            if(recordId) {
                url.current = await getUrl(studyId, recordId);
                setRowColor(recordId, Colors.selectedRow)
            }
        }

        if(url.current)
            await play()
    }

    const forwardSeek = () => {
        const player = document.getElementById(playerId);
        if(player) {
            player.currentTime += seek
        }
    }

    const startRecording = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error("MediaDevices API or getUserMedia not supported");
            return;
        }

        if(localStorage.getItem('recording') === 'true'){
            return
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const _mediaRecorder = new MediaRecorder(stream);
            const audioChunks = [];

            _mediaRecorder.ondataavailable = (event) => {
                audioChunks.push(event.data);
            };

            _mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunks, { type: _mediaRecorder.mimeType });
                const reader = new FileReader();
                reader.readAsDataURL(audioBlob);
                reader.onloadend = () => {
                    const base64data = reader.result;

                    setRecords((prev) => {
                        const item = {
                            id: v4(),
                            base64data,
                            studyId,
                            blobData: audioBlob,
                            chunkData: audioChunks,
                            isRecord: true
                        }

                        console.log(item);

                        return [
                            ...prev,
                            item
                        ]
                    })

                    refreshDataGrid()
                };
            };

            _mediaRecorder.start();
            mediaRecorder.current = _mediaRecorder;
            setIsRecording(true);
            setRecordingTime("00:00");
            recordingInterval.current = setInterval(() => {
                setRecordingTime((prevTime) => {
                    const [minutes, seconds] = prevTime.split(":").map(Number);
                    const newSeconds = seconds + 1;
                    const newMinutes = minutes + Math.floor(newSeconds / 60);
                    return `${String(newMinutes).padStart(2, "0")}:${String(newSeconds % 60).padStart(2, "0")}`;
                });
            }, 1000);

            localStorage.setItem('recording', 'true');
            // Store the mediaRecorder instance to stop it later
        } catch (error) {
            console.error("Error accessing microphone", error);
            localStorage.setItem('recording', 'false');
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current) {
            mediaRecorder.current.stop();
            mediaRecorder.current = null;
            localStorage.setItem('recording', 'false');
            setIsRecording(false);
            if (recordingInterval.current) {
                clearInterval(recordingInterval.current);
            }
        }
    };

    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const saveRecords = async () => {
        const response = await ApiService.VoiceRecord.AddVoiceRecords(studyId, {
            voiceRecordsBase64: records.map(x => x.base64data.split(',')[1])
        })

        if(response.success) {
            setRecords([])
            refreshDataGrid()
        }
    }

    useEffect(() => {
        if(action) {
            if(action.includes('PLAY')) {
                playEvent()
            }
            else if(action.includes('PAUSE')) {
                stop()
            }
            else if(action.includes('FORWARD')) {
                forwardSeek()
            }
            else if(action.includes('BACK')) {
                backwardSeek()
            }
        }
    }, [action]);

    useEffect(() => {
        let columns = [
            {
                title: t('Play'),
                dataIndex: '#',
                key: '#',
                width: 50,
                render: (text, record) => {
                    const {id, studyId} = record;

                    return (
                        <div>
                            <Button
                                size={'small'}
                                type={'text'}
                                onClick={async () => {
                                    if(record.isRecord) {
                                        url.current = record.base64data;
                                        await play()
                                        return
                                    }

                                    url.current = await getUrl(studyId, id)
                                    await play()
                                }}
                            >
                                <PlayCircleOutlined />
                            </Button>
                        </div>
                    )
                }
            },
            {
                title: t('Id'),
                dataIndex: 'id',
                key: 'id',
                columnName: 'id',
                width: 300,
                render: (text, record) => (
                    <VoiceRecordRowMenu
                        row={record}
                        studyId={studyId}
                        onRecordDelete={(row) => {
                            setRecords((prev) => {
                                return prev.filter(x => x.id !== row.id)
                            })
                            refreshDataGrid()
                        }}
                    />
                )
            },
            {
                title: t('User'),
                dataIndex: 'user',
                key: 'user',
                render: (text, record) => record?.user?.visibleName
            },
            {
                title: t('Record Date'),
                dataIndex: 'createdDate',
                key: 'createdDate',
                render: (text, record) => {
                    return dateFormat(text)
                }
            },
        ]

        if(listOther) {
            columns = [
                ...columns,
                {
                    title: t('Id'),
                    dataIndex: 'studyId',
                    key: 'studyId',
                    render: (text, record) => record?.study?.id
                },
                {
                    title: t('Modality'),
                    dataIndex: 'modality',
                    key: 'modality',
                    render: (text, record) => record?.study?.modality?.name
                },
                {
                    title: t('Accession Number'),
                    dataIndex: 'accessionNumber',
                    key: 'accessionNumber',
                    render: (text, record) => record?.study?.accessionNumber
                },
                {
                    title: t('Study Date'),
                    dataIndex: 'studyDate',
                    key: 'studyDate',
                    render: (text, record) => dateFormat(record?.study?.studyDateTime)
                },
                {
                    title: t('Received Date'),
                    dataIndex: 'receivedDate',
                    key: 'receivedDate',
                    render: (text, record) => dateFormat(record?.study?.createdAt)
                },
            ]
        }

        setDataGridProps({
            rowKey: 'id',
            antdTableProps: {
                scroll: {x: !listOther ? 500 : 800},
                pagination: false
            },
            columns,
            rowClassName: (record, index) => {
                let className = 'normal-row';

                if (record.isDeleted)
                    className = 'deleted-row';
                else
                    className = 'normal-row'

                return className;
            },
            onRow: (record) => {
                return {
                    onClick: () => {
                        setRowColor(record.id, Colors.selectedRow);
                    }
                }
            }
        })
    }, [])

    // Tek effect: ayrı [updateKey] ve [listOther] effect'leri mount'ta ikisi birden çalışıp
    // GetList'i (ve ilk kaydın GetFile'ını) iki kez gönderiyordu.
    useEffect(() => {
        getVoiceRecords()
    }, [queryModel.updateKey, listOther])

    useEffect(() => {
        const player = document.getElementById(playerId);
        if(player) {
            player.playbackRate = pbRate
        }
    }, [pbRate]);

    useEffect(() => {
        if(!pressedKey.key)
            return

        if(pressedKey.key.code === 'F8' && !pressedKey?.modifier?.code) {
            playF8()
        }
        else if(pressedKey.key.code === 'F7' && !pressedKey?.modifier?.code) {
            backwardSeek()
        }
        else if(pressedKey.key.code === 'F9' && !pressedKey?.modifier?.code) {
            forwardSeek()
        }
    }, [pressedKey]);

    useEffect(() => {
        // eslint-disable-next-line no-undef
        navigator.mediaSession.metadata = new MediaMetadata({
            title: 'ECloudPacs',
            artist: '',
            album: 'Voice Records'
        })

        navigator.mediaSession.setActionHandler('previoustrack', (e) => {
            backwardSeek()
        })
        navigator.mediaSession.setActionHandler('seekbackward', (e) => {
            backwardSeek()
        })

        navigator.mediaSession.setActionHandler('nexttrack', (e) => {
            forwardSeek()
        })
        navigator.mediaSession.setActionHandler('seekforward', (e) => {
            forwardSeek()
        })

        navigator.mediaSession.setActionHandler('play', async (e) => {
            await playF8()
        })
        navigator.mediaSession.setActionHandler('pause', async (e) => {
            await playF8()
        })

        addKeyEvent([
            {keyCode: 'F7', modifier: null, prevent: true},
            {keyCode: 'F8', modifier: null, prevent: true},
            {keyCode: 'F9', modifier: null, prevent: true},
        ])

        const _seek = localStorage.getItem('seek')
        const _pbRate = localStorage.getItem('pbRate')

        if(_seek)
            setSeek(parseFloat(_seek))

        if(_pbRate)
            setPbRate(parseFloat(_pbRate))
    }, []);

    useEffect(() => {
        return () => {
            if (recordingInterval.current) {
                clearInterval(recordingInterval.current);
            }
        };
    }, []);

    return (
        <>
            <>
                {
                    listOther && (
                        <Typography.Text>
                            {t('OTHER_VOICE_RECORD_TEXT')}
                        </Typography.Text>
                    )
                }
                <DataGrid
                    title={() => (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                            {
                                hasPermission(PERMISSIONS.VOICE_RECORD_ADD) && !listOther && (
                                    <>
                                        <Tooltip title={t('Upload Voice Record')}>
                                            <Button
                                                onClick={uploadVoiceRec}
                                                size={'small'}
                                                icon={<UploadOutlined />}
                                            />
                                        </Tooltip>
                                        <Tooltip title={t('Record Voice')}>
                                            <Button
                                                type={isRecording ? 'danger' : 'success'}
                                                onClick={toggleRecording}
                                                size={'small'}
                                                icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                                            />
                                        </Tooltip>
                                        {isRecording && (
                                            <Typography.Text style={{ fontSize: 12 }}>
                                                {recordingTime}
                                            </Typography.Text>
                                        )}
                                        <Tooltip title={t('Save Records')}>
                                            <Button
                                                disabled={isRecording || records?.length === 0}
                                                type={'info'}
                                                onClick={saveRecords}
                                                size={'small'}
                                                icon={<CheckOutlined />}
                                            />
                                        </Tooltip>
                                    </>
                                )
                            }

                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                                <Select
                                    size={'small'}
                                    style={{ width: 70 }}
                                    options={[
                                        { label: '0.5x', value: 0.5 },
                                        { label: '1x', value: 1 },
                                        { label: '1.25x', value: 1.25 },
                                        { label: '1.5x', value: 1.5 },
                                        { label: '1.75x', value: 1.75 },
                                        { label: '2x', value: 2 },
                                    ]}
                                    onChange={(val) => {
                                        setPbRate(val)
                                        localStorage.setItem('pbRate', val.toString())
                                    }}
                                    value={pbRate}
                                />

                                <Select
                                    size={'small'}
                                    style={{ width: 70 }}
                                    options={[
                                        { label: '100ms', value: 0.1 },
                                        { label: '250ms', value: 0.25 },
                                        { label: '500ms', value: 0.5 },
                                        { label: '1s', value: 1 },
                                        { label: '2s', value: 2 },
                                        { label: '5s', value: 5 },
                                    ]}
                                    onChange={(val) => {
                                        setSeek(val)
                                        localStorage.setItem('seek', val.toString())
                                    }}
                                    value={seek}
                                />
                            </div>
                        </div>
                    )}
                    name={'voice-record'}
                />
            </>
            {
                resultModel?.data?.length > 0 && (
                    <div
                        id={`audio-player-parent-${studyId}`}
                    >
                        <audio
                            onLoadedMetadata={() => {
                                const player = document.getElementById(playerId);
                                if(player) {
                                    player.playbackRate = pbRate
                                }
                            }}
                            id={playerId}
                            controls={true}
                            autoPlay={false}
                            style={{marginTop: 4, width:' 100%'}}
                        />
                    </div>
                )
            }
            <AudioPlayerNavigator
                onBack={() => {setAction('BACK'+new Date().getTime())}}
                onContinue={async () => {setAction('PLAY'+new Date().getTime())}}
                onSeek={() => {setAction('FORWARD'+new Date().getTime())}}
                onStop={async () => {setAction('PAUSE'+new Date().getTime())}}
            />
        </>
    )
}